import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPurchaseBudgetImpact } from '../../api/client'
import { formatCLP, formatNumber } from './format'

/**
 * What the project's money budget looks like from the approver's chair, on one
 * line so it can sit in the action bar beside the button it informs.
 *
 * The question in front of them is not "what has been spent" but "what is
 * left, and does this fit" -- so the remainder AFTER this order is the figure
 * given weight, and it turns red before anybody clicks approve rather than
 * after. Everything else is either small or in the tooltip.
 */
export default function ApprovalBudget({ purchaseId }) {
  const { t } = useTranslation('engineering')
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    getPurchaseBudgetImpact(purchaseId)
      .then(d => { if (!cancelled) setData(d) })
      // A budget panel must never be the reason an approval screen breaks.
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [purchaseId])

  if (failed || !data || !data.has_project) return null

  const hasBudget = data.budget != null
  const over = data.would_exceed
  const unknown = data.order_amount_clp == null

  // Used, then this order, against the budget.
  const pctOf = (v) => (hasBudget && data.budget > 0
    ? Math.min(100, Math.max(0, (v / data.budget) * 100)) : 0)

  const tooltip = [
    data.project_name,
    hasBudget ? `${t('budget.budget')}: ${formatCLP(data.budget)}` : t('budget.noBudget'),
    `${t('budget.approval.remainingBefore')}: ${formatCLP(data.remaining_before)}`,
    data.order_currency !== 'CLP' && data.fx_rate
      ? `${data.order_currency} ${formatNumber(data.order_amount)} × ${formatNumber(data.fx_rate)} (${data.fx_rate_date})`
      : null,
    data.already_counted ? t('budget.approval.alreadyCounted') : null,
    data.missing_rate_orders > 0
      ? t('budget.approval.otherUncounted', { count: data.missing_rate_orders }) : null,
  ].filter(Boolean).join('\n')

  return (
    <div
      title={tooltip}
      className={`flex items-center gap-2.5 text-xs rounded-lg border px-3 py-1.5 whitespace-nowrap ${
        over ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
    >
      <span className="font-semibold text-gray-600">{t('budget.approval.title')}</span>

      {hasBudget && (
        <span className="h-1.5 w-16 rounded-full bg-gray-100 overflow-hidden flex shrink-0">
          <span style={{ width: `${pctOf(data.used_before)}%`,
                         background: over ? '#dc2626' : '#0f766e' }} />
          {!unknown && data.order_amount_clp > 0 && (
            <span style={{ width: `${pctOf(data.order_amount_clp)}%`,
                           background: over ? '#f87171' : '#fbbf24' }} />
          )}
        </span>
      )}

      <span className="text-gray-500 tabular-nums">
        {t('budget.approval.remainingBefore')}{' '}
        <span className="text-gray-700">{formatCLP(data.remaining_before)}</span>
      </span>

      <span className="text-amber-700 tabular-nums">
        {unknown ? t('budget.approval.unknown') : `− ${formatCLP(data.order_amount_clp)}`}
      </span>

      {hasBudget && !unknown && (
        <>
          <span className="text-gray-300">=</span>
          <span className="tabular-nums">
            {t('budget.approval.remainingAfter')}{' '}
            <span className={over ? 'text-red-700 font-semibold' : 'text-gray-900 font-semibold'}>
              {formatCLP(data.remaining_after)}
            </span>
            {data.percent_after != null && (
              <span className="text-gray-400"> · {formatNumber(data.percent_after, 0)}%</span>
            )}
          </span>
        </>
      )}

      {!hasBudget && (
        <span className="text-gray-400">{t('budget.approval.noBudgetShort')}</span>
      )}
      {over && (
        <span className="text-red-700 font-medium">{t('budget.approval.over')}</span>
      )}
      {data.rate_missing && (
        <span className="text-amber-700">
          {t('budget.approval.rateMissingShort', { currency: data.order_currency })}
        </span>
      )}
    </div>
  )
}
